import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Loader2,
  FolderKanban,
  Sparkles,
  Layers,
  Plus,
  Trash2,
  ExternalLink,
  Target,
  CheckCircle2,
  TrendingUp,
  Image as ImageIcon,
  Check,
  Search,
  Star,
  MessageSquareQuote,
  Users,
} from "lucide-react";
import { projectsService } from "@/api/projectsService";
import ProjectMediaManager from "@/components/admin/ProjectMediaManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

/**
 * Helper to generate URL-safe slugs
 */
const slugify = (text) => {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

/**
 * ============================================================
 * ADMIN PROJECT EDITOR (/admin/projects/new & /admin/projects/:id/edit)
 * ============================================================
 * Full-fidelity case study composer and manager.
 * Dynamic metrics, tech stack associations, media assets, and SEO.
 */
export default function AdminProjectEditor() {
  const { slug: paramSlug, id: paramId } = useParams();
  const identifier = paramSlug || paramId;
  const isEditing = Boolean(identifier);
  const navigate = useNavigate();

  const [currentProjectId, setCurrentProjectId] = useState(paramId || null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  // Reference Catalogs
  const [categories, setCategories] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [techSearch, setTechSearch] = useState("");
  const [newTechName, setNewTechName] = useState("");
  const [creatingTech, setCreatingTech] = useState(false);

  // Form Fields
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [isSlugManual, setIsSlugManual] = useState(false);
  const [clientName, setClientName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [projectStatus, setProjectStatus] = useState("Completed");
  const [shortSummary, setShortSummary] = useState("");
  const [challengeDescription, setChallengeDescription] = useState("");
  const [solutionDescription, setSolutionDescription] = useState("");
  const [outcomeDescription, setOutcomeDescription] = useState("");
  const [liveUrl, setLiveUrl] = useState("");
  const [githubUrl, setGithubUrl] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(1);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [testimonialQuote, setTestimonialQuote] = useState("");
  const [testimonialAuthor, setTestimonialAuthor] = useState("");
  const [testimonialRole, setTestimonialRole] = useState("");
  const [testimonialRating, setTestimonialRating] = useState(5);

  // Technology Associations (array of tech IDs)
  const [selectedTechIds, setSelectedTechIds] = useState([]);

  // Dynamic Metrics List: [{ id, label, value, desc }]
  const [metricsList, setMetricsList] = useState([]);

  // Active Tab: 'content' | 'media'
  const [activeTab, setActiveTab] = useState("content");

  // Load initial catalogs and project data if editing
  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const [cats, techs] = await Promise.all([
          projectsService.getCategories(),
          projectsService.getTechnologies(),
        ]);

        if (!mounted) return;
        setCategories(cats);
        setTechnologies(techs);

        if (isEditing) {
          const proj = await projectsService.getProjectBySlugOrId(identifier);
          if (!mounted) return;

          setCurrentProjectId(proj.id);
          if (proj.slug && identifier !== proj.slug) {
            navigate(`/admin/projects/${proj.slug}/edit`, { replace: true });
          }

          setTitle(proj.title || "");
          setSlug(proj.slug || "");
          setIsSlugManual(true);
          setClientName(proj.client_name || "");
          setCategoryId(proj.category_id || (cats[0] ? cats[0].id : ""));
          setProjectStatus(proj.project_status || "Completed");
          setShortSummary(proj.short_summary || "");
          setChallengeDescription(proj.challenge_description || "");
          setSolutionDescription(proj.solution_description || "");
          setOutcomeDescription(proj.outcome_description || "");
          setLiveUrl(proj.live_url || "");
          setGithubUrl(proj.github_url || "");
          setIsPublished(Boolean(proj.is_published));
          setIsFeatured(Boolean(proj.is_featured));
          setDisplayOrder(proj.display_order || 1);
          setMetaTitle(proj.meta_title || "");
          setMetaDescription(proj.meta_description || "");
          setTeamSize(proj.team_size || "");
          setTestimonialQuote(proj.testimonial_quote || "");
          setTestimonialAuthor(proj.testimonial_author || "");
          setTestimonialRole(proj.testimonial_role || "");
          setTestimonialRating(proj.testimonial_rating || 5);

          // Load selected tech IDs
          const techIds = (proj.project_technologies || [])
            .map((pt) => pt.technologies?.id)
            .filter(Boolean);
          setSelectedTechIds(techIds);

          // Load metrics
          if (proj.metrics && typeof proj.metrics === "object") {
            const rawMetrics = Array.isArray(proj.metrics)
              ? proj.metrics
              : Object.entries(proj.metrics).map(([key, val]) => ({
                  id: Math.random().toString(),
                  label: key,
                  value: typeof val === "object" ? val.value : val,
                  desc: typeof val === "object" ? val.desc : "",
                }));
            setMetricsList(rawMetrics);
          }
        } else {
          // Default category to first available
          if (cats.length > 0) setCategoryId(cats[0].id);
        }
      } catch (err) {
        console.error("[AdminProjectEditor] Load error:", err);
        toast.error(err.message || "Failed to load project details.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [identifier, isEditing, navigate]);

  // Auto-slug generator from title
  const handleTitleChange = (val) => {
    setTitle(val);
    if (!isSlugManual) {
      setSlug(slugify(val));
    }
  };

  // Toggle technology selection
  const handleToggleTech = (techId) => {
    setSelectedTechIds((prev) =>
      prev.includes(techId) ? prev.filter((t) => t !== techId) : [...prev, techId]
    );
  };

  // Quick inline creation of a new technology
  const handleCreateTech = async (e) => {
    e.preventDefault();
    if (!newTechName.trim()) return;

    try {
      setCreatingTech(true);
      const created = await projectsService.createTechnology({
        name: newTechName.trim(),
        slug: slugify(newTechName),
      });
      toast.success(`Technology "${created.name}" created.`);
      setTechnologies((prev) => [...prev, created]);
      setSelectedTechIds((prev) => [...prev, created.id]);
      setNewTechName("");
    } catch (err) {
      toast.error(err.message || "Failed to create technology.");
    } finally {
      setCreatingTech(false);
    }
  };

  // Metrics Builder helpers
  const handleAddMetric = () => {
    setMetricsList((prev) => [
      ...prev,
      {
        id: `metric-${Date.now()}`,
        label: "Efficiency Increase",
        value: "40%",
        desc: "Measured across workflow operations",
      },
    ]);
  };

  const handleUpdateMetric = (index, field, value) => {
    setMetricsList((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleRemoveMetric = (index) => {
    setMetricsList((prev) => prev.filter((_, i) => i !== index));
  };

  // Save Project Handler
  const handleSave = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Project title is required.");
      return;
    }
    if (!slug.trim()) {
      toast.error("Valid slug is required.");
      return;
    }
    if (!categoryId) {
      toast.error("Please select a project category.");
      return;
    }

    // Slug format regex validation (must match migration constraint)
    const slugRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    if (!slugRegex.test(slug.trim())) {
      toast.error("Slug format is invalid. Use lowercase letters, numbers, and hyphens (e.g. edumagik-v2).");
      return;
    }

    try {
      setSaving(true);

      const projectPayload = {
        title: title.trim(),
        slug: slug.trim(),
        client_name: clientName.trim() || null,
        category_id: categoryId,
        project_status: projectStatus,
        short_summary: shortSummary.trim() || null,
        challenge_description: challengeDescription.trim() || null,
        solution_description: solutionDescription.trim() || null,
        outcome_description: outcomeDescription.trim() || null,
        metrics: metricsList,
        live_url: liveUrl.trim() || null,
        github_url: githubUrl.trim() || null,
        is_published: isPublished,
        is_featured: isFeatured,
        display_order: Number(displayOrder) || 1,
        meta_title: metaTitle.trim() || null,
        meta_description: metaDescription.trim() || null,
        team_size: teamSize.trim() || null,
        testimonial_quote: testimonialQuote.trim() || null,
        testimonial_author: testimonialAuthor.trim() || null,
        testimonial_role: testimonialRole.trim() || null,
        testimonial_rating: Number(testimonialRating) || 5,
      };

      const targetId = currentProjectId || identifier;
      if (isEditing) {
        await projectsService.updateProject(targetId, projectPayload, selectedTechIds);
        toast.success(`Project "${title}" updated successfully.`);
      } else {
        const created = await projectsService.createProject(projectPayload, selectedTechIds);
        toast.success(`Project "${title}" created successfully.`);
        navigate(`/admin/projects/${created.slug || created.id}/edit`, { replace: true });
      }
    } catch (err) {
      console.error("[AdminProjectEditor] Save error:", err);
      toast.error(err.message || "Failed to save project.");
    } finally {
      setSaving(false);
    }
  };

  // Filter technologies by search query
  const filteredTechnologies = technologies.filter(
    (t) =>
      t.name.toLowerCase().includes(techSearch.toLowerCase()) ||
      t.slug.toLowerCase().includes(techSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="py-24 text-center text-xs text-slate-500 flex flex-col items-center gap-3 font-medium">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
        <span>Loading project details...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-16">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/projects")}
            className="h-9 w-9 p-0 border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 rounded-xl"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-cyan-600" />
              <span>{isEditing ? `Edit: ${title || "Project"}` : "Create New Case Study"}</span>
            </h1>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">
              {isEditing ? `UUID: ${id}` : "Configure project narratives, metrics, and technology associations."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isEditing && (
            <Link
              to={`/projects/${slug}`}
              target="_blank"
              className="h-10 px-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-semibold inline-flex items-center gap-2 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5 text-cyan-600" />
              <span>Preview Live</span>
            </Link>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="h-10 px-6 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold gap-2 shadow-sm"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>{isEditing ? "Save Changes" : "Create Project"}</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Editor Navigation Tabs */}
      <div className="flex items-center border-b border-slate-200 text-xs gap-1">
        <button
          type="button"
          onClick={() => setActiveTab("content")}
          className={cn(
            "px-5 py-2.5 font-semibold transition-colors border-b-2 flex items-center gap-2",
            activeTab === "content"
              ? "border-cyan-600 text-cyan-600"
              : "border-transparent text-slate-500 hover:text-slate-900"
          )}
        >
          <Sparkles className="w-3.5 h-3.5 text-cyan-600" />
          <span>Project Content & Narratives</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("media")}
          className={cn(
            "px-5 py-2.5 font-semibold transition-colors border-b-2 flex items-center gap-2",
            activeTab === "media"
              ? "border-cyan-600 text-cyan-600"
              : "border-transparent text-slate-500 hover:text-slate-900"
          )}
        >
          <ImageIcon className="w-3.5 h-3.5 text-cyan-600" />
          <span>Supabase Media Storage</span>
        </button>
      </div>

      {/* Tab 1: Content & Narratives Form */}
      {activeTab === "content" && (
        <form onSubmit={handleSave} className="space-y-6">
          {/* Section 1: Basic Information */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <h2 className="text-sm font-bold text-cyan-700 uppercase tracking-wider">
              1. Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Project Title *</Label>
                <Input
                  type="text"
                  placeholder="e.g. EduMagik"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-slate-700">URL Slug *</Label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsSlugManual(true);
                      setSlug(slugify(title));
                    }}
                    className="text-[10px] text-cyan-600 hover:underline font-semibold"
                  >
                    Regenerate from Title
                  </button>
                </div>
                <Input
                  type="text"
                  placeholder="e.g. edumagik"
                  value={slug}
                  onChange={(e) => {
                    setIsSlugManual(true);
                    setSlug(e.target.value);
                  }}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 font-mono rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Client / Organization Name</Label>
                <Input
                  type="text"
                  placeholder="e.g. Ravi Srivastava"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Category *</Label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                  required
                >
                  <option value="" disabled className="bg-white text-slate-900">
                    Select a category
                  </option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id} className="bg-white text-slate-900">
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Project Status</Label>
                <select
                  value={projectStatus}
                  onChange={(e) => setProjectStatus(e.target.value)}
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                >
                  <option value="Ongoing" className="bg-white text-slate-900">
                    Ongoing
                  </option>
                  <option value="Completed" className="bg-white text-slate-900">
                    Completed
                  </option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Display Sequence Order</Label>
                <Input
                  type="number"
                  min="1"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Team Allocation / Size</Label>
                <Input
                  type="text"
                  placeholder="e.g. 3-5 Specialists, 4 Engineers"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                />
              </div>
            </div>

            <div className="space-y-1.5 pt-2">
              <Label className="text-xs font-semibold text-slate-700">Short Summary (Preview Card Description)</Label>
              <Input
                type="text"
                placeholder="1-2 sentences summarizing the project engagement..."
                value={shortSummary}
                onChange={(e) => setShortSummary(e.target.value)}
                className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
              />
            </div>
          </div>

          {/* Section 2: Case Study Narratives */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <h2 className="text-sm font-bold text-cyan-700 uppercase tracking-wider">
              2. Case Study Narratives
            </h2>

            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-orange-600 font-semibold">
                <Target className="w-3.5 h-3.5" />
                <span>The Challenge / Business Pain Points</span>
              </div>
              <textarea
                rows={3}
                placeholder="Explain the client's problem, legacy bottlenecks, or technical hurdle..."
                value={challengeDescription}
                onChange={(e) => setChallengeDescription(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-cyan-500 leading-relaxed"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Our Solution / Engineering Delivery</span>
              </div>
              <textarea
                rows={3}
                placeholder="Describe how Shiveda IT Services architected and implemented the solution..."
                value={solutionDescription}
                onChange={(e) => setSolutionDescription(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-cyan-500 leading-relaxed"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-blue-600 font-semibold">
                <TrendingUp className="w-3.5 h-3.5" />
                <span>Business Impact / Final Outcome</span>
              </div>
              <textarea
                rows={3}
                placeholder="Outline the measurable business results, user adoption, and key milestones..."
                value={outcomeDescription}
                onChange={(e) => setOutcomeDescription(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-cyan-500 leading-relaxed"
              />
            </div>
          </div>

          {/* Section 3: Dynamic Metrics Builder */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-cyan-700 uppercase tracking-wider">
                  3. Measurable Project Metrics
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                  Replaces hardcoded placeholders with verified metrics displayed on project detail pages.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddMetric}
                className="h-8 text-xs border-cyan-200 text-cyan-700 hover:bg-cyan-50 rounded-lg gap-1 font-semibold"
              >
                <Plus className="w-3 h-3" />
                <span>Add Metric</span>
              </Button>
            </div>

            {metricsList.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500 font-medium">
                No custom metrics added yet. Click "Add Metric" to define stats (e.g. "40% Efficiency Increase").
              </div>
            ) : (
              <div className="space-y-3">
                {metricsList.map((metric, idx) => (
                  <div
                    key={metric.id || idx}
                    className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/70 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center"
                  >
                    <div className="sm:col-span-3">
                      <Label className="text-[10px] text-slate-600 font-semibold block mb-1">Value / Stat</Label>
                      <Input
                        type="text"
                        placeholder="e.g. 60%"
                        value={metric.value || ""}
                        onChange={(e) => handleUpdateMetric(idx, "value", e.target.value)}
                        className="h-9 bg-white border-slate-200 text-xs text-slate-900 rounded-lg"
                      />
                    </div>
                    <div className="sm:col-span-4">
                      <Label className="text-[10px] text-slate-600 font-semibold block mb-1">Metric Title</Label>
                      <Input
                        type="text"
                        placeholder="e.g. Time Saved"
                        value={metric.label || ""}
                        onChange={(e) => handleUpdateMetric(idx, "label", e.target.value)}
                        className="h-9 bg-white border-slate-200 text-xs text-slate-900 rounded-lg"
                      />
                    </div>
                    <div className="sm:col-span-4">
                      <Label className="text-[10px] text-slate-600 font-semibold block mb-1">Context / Detail</Label>
                      <Input
                        type="text"
                        placeholder="e.g. On manual data entry"
                        value={metric.desc || ""}
                        onChange={(e) => handleUpdateMetric(idx, "desc", e.target.value)}
                        className="h-9 bg-white border-slate-200 text-xs text-slate-900 rounded-lg"
                      />
                    </div>
                    <div className="sm:col-span-1 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMetric(idx)}
                        className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50 rounded-lg mt-4 sm:mt-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 4: Technology Stack Tagging */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-bold text-cyan-700 uppercase tracking-wider">
                  4. Technology Stack Associations
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                  Selected technologies: {selectedTechIds.length} linked.
                </p>
              </div>

              {/* Inline Tech Creation */}
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="New technology name..."
                  value={newTechName}
                  onChange={(e) => setNewTechName(e.target.value)}
                  className="h-8 bg-slate-50 border-slate-200 text-xs text-slate-900 w-48 rounded-lg"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={creatingTech || !newTechName.trim()}
                  onClick={handleCreateTech}
                  className="h-8 px-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  <span>Add Tech</span>
                </Button>
              </div>
            </div>

            {/* Filter Search */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="text"
                placeholder="Filter technologies list..."
                value={techSearch}
                onChange={(e) => setTechSearch(e.target.value)}
                className="pl-8 h-8 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-lg max-w-sm"
              />
            </div>

            {/* Tech Badges Grid */}
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 rounded-xl border border-slate-200 bg-slate-50/50">
              {filteredTechnologies.map((tech) => {
                const isSelected = selectedTechIds.includes(tech.id);
                return (
                  <button
                    key={tech.id}
                    type="button"
                    onClick={() => handleToggleTech(tech.id)}
                    className={cn(
                      "px-3 py-1 rounded-lg text-xs font-medium transition-all duration-150 flex items-center gap-1.5 border",
                      isSelected
                        ? "bg-cyan-50 text-cyan-700 border-cyan-300 font-semibold shadow-xs"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
                    )}
                  >
                    {isSelected && <Check className="w-3 h-3 text-cyan-600" />}
                    <span>{tech.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Section 5: Client Testimonial & Feedback */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-cyan-700 uppercase tracking-wider flex items-center gap-2">
                  <MessageSquareQuote className="w-4 h-4 text-cyan-600" />
                  <span>5. Client Testimonial & Review (CMS-Managed)</span>
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                  Verified client feedback displayed on the project case study and eligible for the Home page trust showcase.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Client Testimonial Quote</Label>
                <textarea
                  rows={3}
                  placeholder="Direct client review or quote regarding project delivery and collaboration..."
                  value={testimonialQuote}
                  onChange={(e) => setTestimonialQuote(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-cyan-500 leading-relaxed"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Reviewer Name</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Jitendra Puri"
                    value={testimonialAuthor}
                    onChange={(e) => setTestimonialAuthor(e.target.value)}
                    className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Reviewer Title / Role</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Founder & Director, JobsCare"
                    value={testimonialRole}
                    onChange={(e) => setTestimonialRole(e.target.value)}
                    className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Client Rating</Label>
                  <select
                    value={testimonialRating}
                    onChange={(e) => setTestimonialRating(Number(e.target.value))}
                    className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                  >
                    <option value={5} className="bg-white text-slate-900">5 Stars (Exceptional)</option>
                    <option value={4} className="bg-white text-slate-900">4 Stars (Great)</option>
                    <option value={3} className="bg-white text-slate-900">3 Stars (Good)</option>
                    <option value={2} className="bg-white text-slate-900">2 Stars</option>
                    <option value={1} className="bg-white text-slate-900">1 Star</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6: External Links & Visibility */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <h2 className="text-sm font-bold text-cyan-700 uppercase tracking-wider">
              6. Publishing & URLs
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Live Website URL</Label>
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={liveUrl}
                  onChange={(e) => setLiveUrl(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">GitHub Repository URL (Optional)</Label>
                <Input
                  type="url"
                  placeholder="https://github.com/..."
                  value={githubUrl}
                  onChange={(e) => setGithubUrl(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                />
              </div>
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-6 border-t border-slate-100">
              <label className="flex items-center gap-3 cursor-pointer group select-none">
                <div
                  onClick={() => setIsPublished((v) => !v)}
                  className={cn(
                    "w-10 h-5 rounded-full relative transition-colors cursor-pointer",
                    isPublished ? "bg-cyan-600" : "bg-slate-200"
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                      isPublished ? "left-5" : "left-0.5"
                    )}
                  />
                </div>
                <span className="text-xs font-semibold text-slate-900 group-hover:text-cyan-700 transition-colors">
                  Publish to Live Website
                </span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group select-none">
                <div
                  onClick={() => setIsFeatured((v) => !v)}
                  className={cn(
                    "w-10 h-5 rounded-full relative transition-colors cursor-pointer",
                    isFeatured ? "bg-cyan-600" : "bg-slate-200"
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-all",
                      isFeatured ? "left-5" : "left-0.5"
                    )}
                  />
                </div>
                <span className="text-xs font-semibold text-slate-900 group-hover:text-cyan-700 transition-colors">
                  Feature on Home Page
                </span>
              </label>
            </div>
          </div>

          {/* Section 7: SEO Metadata */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <h2 className="text-sm font-bold text-cyan-700 uppercase tracking-wider">
              7. Search Engine Optimization (SEO)
            </h2>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-slate-700">Meta Title</Label>
                  <span className="text-[10px] text-slate-400 font-medium">{metaTitle.length}/60 chars</span>
                </div>
                <Input
                  type="text"
                  placeholder="e.g. EduMagik Case Study | Shiveda IT Services"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-slate-700">Meta Description</Label>
                  <span className="text-[10px] text-slate-400 font-medium">{metaDescription.length}/160 chars</span>
                </div>
                <textarea
                  rows={2}
                  placeholder="Concise, keyword-rich overview for search results snippet..."
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-cyan-500 leading-relaxed"
                />
              </div>
            </div>
          </div>

          {/* Bottom Save Bar */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/projects")}
              className="h-11 px-6 rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-semibold"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={saving}
              className="h-11 px-8 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold gap-2 shadow-sm"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving Project...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isEditing ? "Save Changes" : "Create Project"}</span>
                </>
              )}
            </Button>
          </div>
        </form>
      )}

      {/* Tab 2: Supabase Storage Media Manager */}
      {activeTab === "media" && (
        <ProjectMediaManager projectId={currentProjectId || identifier} />
      )}
    </div>
  );
}
